import { sub, sum } from "@dantria/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, api, fmtAmount as fmt, unwrap } from "../api";
import {
  Button,
  DeleteButton,
  Empty,
  ErrorNote,
  Loading,
  Panel,
  Table,
  TableSearch,
  Td,
  Th,
  useTable,
} from "../ui";
import { useDeleteRecord } from "../useDeleteRecord";
import { CommitmentForm } from "./shared";

export function Payables({
  slug,
  currency,
  role,
  onOpenContractor,
}: { slug: string; currency: string; role: string; onOpenContractor: (id: string) => void }) {
  const qc = useQueryClient();
  const del = useDeleteRecord(slug);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);

  const commitments = useQuery({
    queryKey: ["commitments", slug],
    queryFn: async () =>
      unwrap(await api.api.c[":slug"].payables.commitments.$get({ param: { slug }, query: {} })),
  });
  const bills = useQuery({
    queryKey: ["bills", slug],
    queryFn: async () =>
      unwrap(await api.api.c[":slug"].payables.bills.$get({ param: { slug }, query: {} })),
  });
  const summary = useQuery({
    queryKey: ["cost-summary", slug],
    queryFn: async () =>
      unwrap(await api.api.c[":slug"].payables["cost-summary"].$get({ param: { slug } })),
  });
  const projects = useQuery({
    queryKey: ["projects", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].projects.$get({ param: { slug } })),
  });
  const costCodes = useQuery({
    queryKey: ["cost-codes", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"]["cost-codes"].$get({ param: { slug } })),
  });

  function refresh() {
    for (const k of ["commitments", "bills", "cost-summary", "cost-summary-contractors"]) {
      qc.invalidateQueries({ queryKey: [k, slug] });
    }
  }

  const run = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onMutate: () => setError(null),
    onSuccess: refresh,
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : "Something went wrong. Try again."),
  });

  // One row per contractor rather than per commitment — a contractor on
  // three jobs used to appear as three unrelated rows, with nothing on the
  // page to say they were the same AP relationship. Opening a group is what
  // ContractorDetail is for; the commitment-level detail (draws, earnings)
  // lives there now instead of inline here.
  const byContractor = new Map<
    string,
    {
      contractorId: string;
      contractorName: string;
      count: number;
      value: string[];
      billed: string[];
      outstanding: string[];
    }
  >();
  for (const cm of commitments.data?.commitments ?? []) {
    const g = byContractor.get(cm.contractorId);
    if (g) {
      g.count += 1;
      g.value.push(cm.contractValue);
      g.billed.push(cm.billed);
      g.outstanding.push(cm.outstanding);
    } else {
      byContractor.set(cm.contractorId, {
        contractorId: cm.contractorId,
        contractorName: cm.contractorName,
        count: 1,
        value: [cm.contractValue],
        billed: [cm.billed],
        outstanding: [cm.outstanding],
      });
    }
  }
  const contractorGroups = [...byContractor.values()].map((g) => ({
    contractorId: g.contractorId,
    contractorName: g.contractorName,
    count: g.count,
    committed: sum(g.value),
    billed: sum(g.billed),
    outstanding: sum(g.outstanding),
  }));

  const contractorsTable = useTable(contractorGroups, {
    filter: (g, q) => g.contractorName.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      contractor: (g) => g.contractorName.toLowerCase(),
      count: (g) => g.count,
      committed: (g) => Number(g.committed),
      billed: (g) => Number(g.billed),
      stillToCome: (g) => Number(sub(g.committed, g.billed)),
      outstanding: (g) => Number(g.outstanding),
    },
  });

  const billsTable = useTable(bills.data?.bills ?? [], {
    filter: (b, q) =>
      `${b.contractorName} ${b.number ?? ""} ${b.status}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      contractor: (b) => b.contractorName.toLowerCase(),
      ref: (b) => (b.number ?? "").toLowerCase(),
      due: (b) => b.dueDate,
      status: (b) => b.status,
      retainage: (b) => Number(b.retainageWithheld),
      total: (b) => Number(b.total),
      paid: (b) => Number(b.amountPaid),
      outstanding: (b) => Number(b.outstanding),
    },
  });

  const costSummaryTable = useTable(summary.data?.projects ?? [], {
    filter: (p, q) => `${p.code} ${p.name}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      project: (p) => p.name.toLowerCase(),
      committed: (p) => Number(p.committed),
      billed: (p) => Number(p.billed),
      paid: (p) => Number(p.paid),
      remaining: (p) => Number(p.remaining),
    },
  });

  if (commitments.isPending || !commitments.data) return <Loading />;

  // A contractor can be added inline from the commitment form itself, so only
  // a project — which can't — has to exist before opening it.
  const ready = (projects.data?.projects.length ?? 0) > 0;

  return (
    <>
      {error && <ErrorNote>{error}</ErrorNote>}
      {del.error && <ErrorNote>{del.error}</ErrorNote>}

      <Panel
        title="Commitments"
        action={
          ready ? (
            <Button onClick={() => setShowNew(!showNew)}>
              {showNew ? "Cancel" : "New commitment"}
            </Button>
          ) : undefined
        }
      >
        <p className="mb-3 max-w-prose text-sm text-neutral-500">
          What you've agreed to pay a contractor. Committed cost counts against the project from the
          moment you sign, not when the first bill arrives — which is what makes cost-to-come a real
          number.
        </p>

        {!ready && (
          <Empty title="Add a project first">A commitment ties a contractor to a project.</Empty>
        )}

        {showNew && ready && (
          <CommitmentForm
            slug={slug}
            currency={currency}
            projects={projects.data?.projects ?? []}
            costCodes={costCodes.data?.costCodes ?? []}
            busy={run.isPending}
            onSubmit={(body) => {
              run.mutate(() =>
                unwrap(
                  api.api.c[":slug"].payables.commitments.$post({
                    param: { slug },
                    json: body,
                  }),
                ),
              );
              setShowNew(false);
            }}
          />
        )}

        {contractorGroups.length === 0 ? (
          ready && <Empty title="No commitments yet" />
        ) : (
          <>
            <TableSearch
              value={contractorsTable.query}
              onChange={contractorsTable.setQuery}
              placeholder="Filter contractors…"
            />
            <Table
              head={
                <>
                  <Th
                    sortKey="contractor"
                    active={contractorsTable.sortKey === "contractor"}
                    dir={contractorsTable.dir}
                    onSort={contractorsTable.toggleSort}
                  >
                    Contractor
                  </Th>
                  <Th
                    right
                    sortKey="count"
                    active={contractorsTable.sortKey === "count"}
                    dir={contractorsTable.dir}
                    onSort={contractorsTable.toggleSort}
                  >
                    Commitments
                  </Th>
                  <Th
                    right
                    sortKey="committed"
                    active={contractorsTable.sortKey === "committed"}
                    dir={contractorsTable.dir}
                    onSort={contractorsTable.toggleSort}
                  >
                    Committed
                  </Th>
                  <Th
                    right
                    sortKey="billed"
                    active={contractorsTable.sortKey === "billed"}
                    dir={contractorsTable.dir}
                    onSort={contractorsTable.toggleSort}
                  >
                    Billed
                  </Th>
                  <Th
                    right
                    sortKey="stillToCome"
                    active={contractorsTable.sortKey === "stillToCome"}
                    dir={contractorsTable.dir}
                    onSort={contractorsTable.toggleSort}
                  >
                    Still to come
                  </Th>
                  <Th
                    right
                    sortKey="outstanding"
                    active={contractorsTable.sortKey === "outstanding"}
                    dir={contractorsTable.dir}
                    onSort={contractorsTable.toggleSort}
                  >
                    Owed
                  </Th>
                </>
              }
            >
              {contractorsTable.rows.map((g) => (
                <tr
                  key={g.contractorId}
                  tabIndex={0}
                  onClick={() => onOpenContractor(g.contractorId)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onOpenContractor(g.contractorId);
                  }}
                  className="cursor-pointer border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100 focus:bg-neutral-100 focus:outline-none"
                >
                  <Td>{g.contractorName}</Td>
                  <Td right mono muted>
                    {g.count}
                  </Td>
                  <Td right mono>
                    {fmt(g.committed)}
                  </Td>
                  <Td right mono muted>
                    {fmt(g.billed)}
                  </Td>
                  <Td right mono muted>
                    {fmt(sub(g.committed, g.billed))}
                  </Td>
                  <Td right mono>
                    {fmt(g.outstanding)}
                  </Td>
                </tr>
              ))}
            </Table>
          </>
        )}
      </Panel>

      {(role === "owner" || role === "admin") && (
        <PendingContractorRequests
          slug={slug}
          bills={(bills.data?.bills ?? []).filter((b) => b.status === "draft")}
          busy={run.isPending}
          onApprove={(id) =>
            run.mutate(() =>
              unwrap(
                api.api.c[":slug"].payables.bills[":id"].approve.$post({ param: { slug, id } }),
              ),
            )
          }
          onReject={(id) =>
            run.mutate(() =>
              unwrap(
                api.api.c[":slug"].payables.bills[":id"].reject.$post({ param: { slug, id } }),
              ),
            )
          }
        />
      )}

      <Panel title="Bills">
        {bills.isPending ? (
          <Loading />
        ) : !bills.data?.bills.length ? (
          <Empty title="No bills yet">Bill a draw above once a contractor claims it.</Empty>
        ) : (
          <>
            <TableSearch
              value={billsTable.query}
              onChange={billsTable.setQuery}
              placeholder="Filter bills…"
            />
            <Table
              head={
                <>
                  <Th
                    sortKey="contractor"
                    active={billsTable.sortKey === "contractor"}
                    dir={billsTable.dir}
                    onSort={billsTable.toggleSort}
                  >
                    Contractor
                  </Th>
                  <Th
                    sortKey="ref"
                    active={billsTable.sortKey === "ref"}
                    dir={billsTable.dir}
                    onSort={billsTable.toggleSort}
                  >
                    Ref
                  </Th>
                  <Th
                    sortKey="due"
                    active={billsTable.sortKey === "due"}
                    dir={billsTable.dir}
                    onSort={billsTable.toggleSort}
                  >
                    Due
                  </Th>
                  <Th
                    sortKey="status"
                    active={billsTable.sortKey === "status"}
                    dir={billsTable.dir}
                    onSort={billsTable.toggleSort}
                  >
                    Status
                  </Th>
                  <Th
                    right
                    sortKey="retainage"
                    active={billsTable.sortKey === "retainage"}
                    dir={billsTable.dir}
                    onSort={billsTable.toggleSort}
                  >
                    Retainage
                  </Th>
                  <Th
                    right
                    sortKey="total"
                    active={billsTable.sortKey === "total"}
                    dir={billsTable.dir}
                    onSort={billsTable.toggleSort}
                  >
                    Total
                  </Th>
                  <Th
                    right
                    sortKey="paid"
                    active={billsTable.sortKey === "paid"}
                    dir={billsTable.dir}
                    onSort={billsTable.toggleSort}
                  >
                    Paid
                  </Th>
                  <Th
                    right
                    sortKey="outstanding"
                    active={billsTable.sortKey === "outstanding"}
                    dir={billsTable.dir}
                    onSort={billsTable.toggleSort}
                  >
                    Owed
                  </Th>
                  {role === "owner" && <Th />}
                </>
              }
            >
              {billsTable.rows.map((b) => (
                <tr
                  key={b.id}
                  className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                >
                  <Td>
                    <button
                      type="button"
                      onClick={() => onOpenContractor(b.contractorId)}
                      className="text-left hover:underline"
                    >
                      {b.contractorName}
                    </button>
                  </Td>
                  <Td mono muted>
                    {b.number ?? "—"}
                  </Td>
                  <Td mono muted>
                    {b.dueDate}
                  </Td>
                  <Td>
                    {b.status}
                    {b.overdue && <span className="ml-2 text-xs text-red-600">overdue</span>}
                  </Td>
                  <Td right mono muted>
                    {fmt(b.retainageWithheld)}
                  </Td>
                  <Td right mono>
                    {fmt(b.total)}
                  </Td>
                  <Td right mono muted>
                    {fmt(b.amountPaid)}
                  </Td>
                  <Td right mono>
                    {fmt(b.outstanding)}
                  </Td>
                  {role === "owner" && (
                    <Td right>
                      <DeleteButton
                        busy={del.deleting}
                        onConfirm={() => del.remove("bill", b.id)}
                      />
                    </Td>
                  )}
                </tr>
              ))}
            </Table>
          </>
        )}
      </Panel>

      <Panel title="Committed vs billed">
        <p className="mb-3 max-w-prose text-sm text-neutral-500">
          Committed here is the whole of what you signed, so it can be read against what has been
          billed out of it. "Still to come" is the part that hasn't — and it is that figure, not the
          signed total, that the project's committed cost reports, since a billed draw has already
          become direct cost.
        </p>
        {summary.isPending ? (
          <Loading />
        ) : !summary.data?.projects.length ? (
          <Empty title="No projects yet" />
        ) : (
          <>
            <TableSearch
              value={costSummaryTable.query}
              onChange={costSummaryTable.setQuery}
              placeholder="Filter projects…"
            />
            <Table
              head={
                <>
                  <Th
                    sortKey="project"
                    active={costSummaryTable.sortKey === "project"}
                    dir={costSummaryTable.dir}
                    onSort={costSummaryTable.toggleSort}
                  >
                    Project
                  </Th>
                  <Th
                    right
                    sortKey="committed"
                    active={costSummaryTable.sortKey === "committed"}
                    dir={costSummaryTable.dir}
                    onSort={costSummaryTable.toggleSort}
                  >
                    Committed
                  </Th>
                  <Th
                    right
                    sortKey="billed"
                    active={costSummaryTable.sortKey === "billed"}
                    dir={costSummaryTable.dir}
                    onSort={costSummaryTable.toggleSort}
                  >
                    Billed
                  </Th>
                  <Th
                    right
                    sortKey="paid"
                    active={costSummaryTable.sortKey === "paid"}
                    dir={costSummaryTable.dir}
                    onSort={costSummaryTable.toggleSort}
                  >
                    Paid
                  </Th>
                  <Th
                    right
                    sortKey="remaining"
                    active={costSummaryTable.sortKey === "remaining"}
                    dir={costSummaryTable.dir}
                    onSort={costSummaryTable.toggleSort}
                  >
                    Still to come
                  </Th>
                </>
              }
            >
              {costSummaryTable.rows.map((p) => (
                <tr
                  key={p.projectId}
                  className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                >
                  <Td>
                    <span className="tabular text-neutral-500">{p.code}</span> {p.name}
                  </Td>
                  <Td right mono>
                    {fmt(p.committed)}
                  </Td>
                  <Td right mono muted>
                    {fmt(p.billed)}
                  </Td>
                  <Td right mono muted>
                    {fmt(p.paid)}
                  </Td>
                  {/* The number neither committed nor billed gives on its own. */}
                  <Td right mono>
                    {fmt(p.remaining)}
                  </Td>
                </tr>
              ))}
            </Table>
          </>
        )}
      </Panel>
    </>
  );
}

/**
 * Bills a contractor submitted through their own portal — status `draft`,
 * meaning nothing has posted to the ledger yet. Approving runs the same
 * posting `POST /payables/bills` does for a staff-entered bill; rejecting
 * voids it and frees the draw so the contractor can submit again.
 */
function PendingContractorRequests({
  slug,
  bills,
  busy,
  onApprove,
  onReject,
}: {
  slug: string;
  bills: {
    id: string;
    contractorName: string;
    projectName: string;
    number: string | null;
    total: string;
    currency: string;
  }[];
  busy: boolean;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}) {
  if (bills.length === 0) return null;

  return (
    <Panel title="Pending contractor requests">
      <p className="mb-3 max-w-prose text-sm text-neutral-500">
        Submitted from the contractor portal against the next open draw. Nothing here has posted to
        the books yet — approving does, and rejecting frees the draw so the contractor can submit
        again.
      </p>
      <Table
        maxHeight={false}
        head={
          <>
            <Th>Contractor</Th>
            <Th>Project</Th>
            <Th>Ref</Th>
            <Th right>Amount</Th>
            <Th />
          </>
        }
      >
        {bills.map((b) => (
          <tr key={`${slug}-${b.id}`} className="border-b border-neutral-100 last:border-0">
            <Td>{b.contractorName}</Td>
            <Td muted>{b.projectName}</Td>
            <Td mono muted>
              {b.number ?? "—"}
            </Td>
            <Td right mono>
              {fmt(b.total, b.currency)}
            </Td>
            <Td right>
              <div className="flex justify-end gap-1">
                <Button variant="ghost" disabled={busy} onClick={() => onApprove(b.id)}>
                  Approve
                </Button>
                <Button
                  variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm(`Reject this request from ${b.contractorName}?`)) {
                      onReject(b.id);
                    }
                  }}
                >
                  Reject
                </Button>
              </div>
            </Td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}
